import update from 'immutability-helper';
import MessageHandler from 'message-handler';
import FormSwiftAPIClient from 'FormSwiftAPI/FormSwiftAPIClient';
import StaticBuilderEnum from 'StaticBuilder/StaticBuilderConstants';
import { finishStaticBuilderSaveProgressBarAnimation } from 'Animation/AnimationActions';
import { setStaticBuilderNavStep } from 'StaticBuilder/StaticBuilderActions';
import SelfServeBuilderEnum from 'SelfServeBuilder/SelfServeBuilderConstants';
import SelfServeRecipientBuilderEnum from './SelfServeRecipientBuilderConstants';

const uuidV4 = require('uuid/v4');

export const formSwiftAPIClient = new FormSwiftAPIClient(window.API_URL);

export const getSectionDataForShare = shareKey => (
  async (dispatch) => {
    dispatch({
      type: SelfServeRecipientBuilderEnum.SELF_SERVE_RECIPIENT_BUILDER_SECTION_DATA_GET_REQUEST,
    });
    try {
      const payload = {
        // ie11 caches fetch requests, in order to ensure we fetch latest field_outline
        // (and prevent blowing away data), add uuid to make every request unique
        version: uuidV4(),
      };
      const response = await formSwiftAPIClient.request('GET', `/custom-template/by-document-share/${shareKey}`, payload);

      const sectionData = response.data.field_outline;
      const userData = response.data.user_data || {};
      const documentType = response.data.document_type;
      const documentShareFinished = response.data.document_share_finished;
      const accessLevel = response.data.access_level;
      dispatch({
        type: StaticBuilderEnum.STATIC_BUILDER_FIELD_OUTLINE_SET,
        fieldOutline: sectionData,
      });

      dispatch({
        type: StaticBuilderEnum.STATIC_BUILDER_SESSION_CONTENT_GET_SUCCESS,
        data: {
          content: userData.data || null,
        },
        documentType,
      });

      dispatch({
        type: SelfServeBuilderEnum.SELF_SERVE_BUILDER_LOADING_DONE,
        loading: false,
      });
      if (documentShareFinished) {
        dispatch({
          type: SelfServeRecipientBuilderEnum.SELF_SERVE_RECIPIENT_BUILDER_SET_FINISHED,
          accessLevel,
        });
      }
    } catch (e) {
      dispatch({
        type: SelfServeRecipientBuilderEnum.SELF_SERVE_RECIPIENT_BUILDER_SECTION_DATA_GET_FAILURE,
        error: e.message,
      });

      window.location.assign(MessageHandler.getErrorRedirectUrl(e.message));
    }
  }
);


export const loadSelfServeRecipientBuilderSection = sectionStep => (
  (dispatch) => {
    dispatch({
      type: StaticBuilderEnum.STATIC_BUILDER_SECTION_LOAD_REQUEST,
    });
    dispatch(setStaticBuilderNavStep(sectionStep));
    dispatch({
      type: StaticBuilderEnum.STATIC_BUILDER_SECTION_LOAD_SUCCESS,
    });

    dispatch(finishStaticBuilderSaveProgressBarAnimation());
  }
);


export const loadNextSelfServeRecipientBuilderSection = () => (
  (dispatch, getState) => {
    const state = getState();
    const {
      step: onStep,
      fieldOutline,
    } = state.StaticBuilderReducer;

    const nextStep = onStep + 1;
    if (nextStep >= fieldOutline.sections.length) {
      return false;
    }

    dispatch(loadSelfServeRecipientBuilderSection(nextStep));

    return true;
  }
);

export const loadPreviousSelfServeRecipientBuilderSection = () => (
  (dispatch, getState) => {
    const state = getState();
    const {
      step: onStep,
    } = state.StaticBuilderReducer;

    const previousStep = onStep - 1;
    if (previousStep < 0) {
      return false;
    }

    dispatch(loadSelfServeRecipientBuilderSection(previousStep));
    return true;
  }
);

export const saveSelfServeRecipientBuilderSectionContent = (
  sectionContent,
  shareKey,
  isPreviewMode = false,
) => (
  async (dispatch, getState) => {
    try {
      const state = getState();
      const {
        fieldData,
      } = state.StaticBuilderReducer;

      const payload = {
        content: update(fieldData, {
          $merge: sectionContent,
        }),
      };
      if (!isPreviewMode) {
        await formSwiftAPIClient.request(
          'POST',
          `/document-share/session/content/${shareKey}`,
          payload,
        );
      }

      dispatch({
        type: StaticBuilderEnum.STATIC_BUILDER_SECTION_CONTENT_SAVE_SUCCESS,
        data: payload.content,
      });

      return true;
    } catch (e) {
      return false;
    }
  }
);

export const saveAndContinue = (sectionContent, shareKey) => (
  async (dispatch) => {
    const isSuccess = await dispatch(
      saveSelfServeRecipientBuilderSectionContent(sectionContent, shareKey),
    );

    if (isSuccess) {
      dispatch(loadNextSelfServeRecipientBuilderSection());
    }
  }
);

export const saveAndContinuePreview = (sectionContent, currentStep, totalSteps) => (
  (dispatch, getState) => {
    const state = getState();
    const { fieldData } = state.StaticBuilderReducer;

    const payload = {
      content: update(fieldData, {
        $merge: sectionContent,
      }),
    };

    dispatch({
      type: StaticBuilderEnum.STATIC_BUILDER_SECTION_CONTENT_SAVE_SUCCESS,
      data: payload.content,
    });

    const nextStep = currentStep + 1;
    if (nextStep >= totalSteps) return;
    dispatch(loadSelfServeRecipientBuilderSection(nextStep));
  }
);

export const finishAndSend = shareKey => (
  async (dispatch) => {
    try {
      await formSwiftAPIClient.request('POST', `/document-share/${shareKey}/finish`);
      dispatch({
        type: SelfServeRecipientBuilderEnum.SELF_SERVE_RECIPIENT_BUILDER_SET_FINISHED,
      });
    } catch (e) {
      dispatch({
        type: SelfServeRecipientBuilderEnum.SELF_SERVE_RECIPIENT_BUILDER_SET_FINISHED_FAILURE,
        error: e.message,
      });
    }
  }
);

export const associateDocumentShare = shareKey => (
  async (dispatch) => {
    try {
      const payload = {
        user_id: window.COOKIE.get('userid'),
        user_token: window.COOKIE.get('userToken'),
      };
      await formSwiftAPIClient.request(
        'POST',
        `/document-share/${shareKey}/associate-user`,
        payload,
      );
      dispatch({
        type: SelfServeRecipientBuilderEnum.SELF_SERVE_RECIPIENT_BUILDER_DOCUMENT_SHARE_ASSOCIATE_SUCCESS,
      });
    } catch (e) {
      dispatch({
        type: SelfServeRecipientBuilderEnum.SELF_SERVE_RECIPIENT_BUILDER_DOCUMENT_SHARE_ASSOCIATE_FAILURE,
        error: e.message,
      });
    }
  }
);

export const highlightField = fieldId => (
  (dispatch) => {
    dispatch({
      type: SelfServeRecipientBuilderEnum.SELF_SERVE_RECIPIENT_BUILDER_HIGHLIGHT_FIELD,
      fieldId,
    });
  }
);



// WEBPACK FOOTER //
// ./src-react/SelfServeRecipientBuilder/SelfServeRecipientBuilderActions.jsx